import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Building2, CheckCircle2, XCircle, AlertTriangle, Clock, Repeat } from "lucide-react";
import { toast, Toaster } from "sonner";
import USDateInput from "../components/USDateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RenewalProposal() {
  const { token } = useParams();
  const [proposal, setProposal] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(null); // "accept" | "decline" | "counter" | null
  const [tenantMessage, setTenantMessage] = useState("");
  const [counterOpen, setCounterOpen] = useState(false);
  const [counterRent, setCounterRent] = useState("");
  const [counterEnd, setCounterEnd] = useState("");
  const [counterDeposit, setCounterDeposit] = useState("");

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/renewal-proposals/${token}`).then(
      (r) => {
        if (!cancelled) {
          setProposal(r.data);
          // Pre-fill counter form with current proposal values for one-tap edit
          setCounterRent(String(r.data.monthly_rent ?? ""));
          setCounterEnd(r.data.end_date || "");
          setCounterDeposit(String(r.data.deposit ?? ""));
        }
      },
      (err) => {
        if (!cancelled) setError(err.response?.data?.detail || "Could not load this renewal offer.");
      },
    );
    return () => { cancelled = true; };
  }, [token]);

  const respond = async (decision) => {
    setSubmitting(decision);
    try {
      const payload = { decision, tenant_message: tenantMessage };
      if (decision === "counter") {
        const rent = Number(counterRent);
        const dep = Number(counterDeposit);
        if (!rent || rent <= 0) { toast.error("Enter a positive rent"); setSubmitting(null); return; }
        if (!counterEnd) { toast.error("Pick an end date"); setSubmitting(null); return; }
        if (dep < 0) { toast.error("Deposit cannot be negative"); setSubmitting(null); return; }
        payload.monthly_rent = rent;
        payload.end_date = counterEnd;
        payload.deposit = dep;
      }
      const r = await axios.post(`${API}/public/renewal-proposals/${token}/respond`, payload);
      setProposal((prev) => ({
        ...prev,
        status: r.data.status,
        tenant_message: tenantMessage || prev?.tenant_message,
        tenant_counter: decision === "counter" ? {
          monthly_rent: payload.monthly_rent,
          end_date: payload.end_date,
          deposit: payload.deposit,
          tenant_message: tenantMessage || null,
        } : prev?.tenant_counter,
      }));
      toast.success(
        decision === "accept" ? "Renewal accepted" :
        decision === "counter" ? "Counter-offer sent — your landlord will be in touch" :
        "Response sent",
      );
      setCounterOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit your response");
    } finally {
      setSubmitting(null);
    }
  };

  const fmtCurrency = useMemo(() => (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, []);

  if (error) {
    return (
      <Page>
        <div className="text-center" data-testid="proposal-error">
          <AlertTriangle className="w-12 h-12 text-rose-500 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit" }}>This link isn't valid</h1>
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </Page>
    );
  }
  if (!proposal) {
    return <Page><div className="text-sm text-slate-400 text-center" data-testid="proposal-loading">Loading…</div></Page>;
  }

  const isPending = proposal.status === "pending";
  const isCountered = proposal.status === "countered";
  const isResolved = proposal.status === "accepted" || proposal.status === "declined";

  return (
    <Page>
      <Toaster richColors position="top-right" />
      <div className="max-w-xl mx-auto" data-testid="renewal-proposal-page">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-3">
          <Building2 className="w-3.5 h-3.5" /> RealtyVault Manager · Lease renewal offer
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit" }}>
          {isPending ? "Your landlord is offering to renew your lease" :
           isCountered ? "Counter-offer sent" :
           proposal.status === "accepted" ? "Renewal accepted" :
           proposal.status === "declined" ? "Renewal declined" : "Renewal offer"}
        </h1>
        <p className="text-sm text-slate-500 mb-6">
          {isCountered
            ? "Your landlord has been notified of the terms below and will accept, decline, or send a revised offer. We'll email you with the next step."
            : "Review the proposed terms below. Accepting creates an active lease for the new term immediately. You can decline or counter-offer instead."}
        </p>

        <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6 shadow-sm">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Property</div>
          <div className="font-semibold text-slate-900 mb-4" data-testid="proposal-property">
            {proposal.property_name || "—"} · {proposal.unit_label || "Unit"}
          </div>

          <Row label="Tenant" value={proposal.tenant_name || "—"} sub={proposal.tenant_email} />
          <Row label="Proposed term" value={`${proposal.start_date}  →  ${proposal.end_date}`} mono />
          <Row label="Monthly rent" value={fmtCurrency(proposal.monthly_rent)} mono testid="proposal-rent" />
          <Row label="Deposit" value={fmtCurrency(proposal.deposit)} mono />

          {proposal.landlord_message ? (
            <div className="mt-5 bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs text-slate-700">
              <div className="font-semibold text-slate-900 mb-1">A note from your landlord</div>
              <div className="whitespace-pre-line leading-relaxed" data-testid="proposal-landlord-message">{proposal.landlord_message}</div>
            </div>
          ) : null}

          {isCountered && proposal.tenant_counter ? (
            <div className="mt-5 bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs" data-testid="counter-summary">
              <div className="font-semibold text-amber-900 mb-1 uppercase tracking-wider text-[10px]">Your counter-offer</div>
              <div className="text-amber-900 leading-relaxed">
                Rent: <span className="font-mono font-bold">${Number(proposal.tenant_counter.monthly_rent || 0).toLocaleString()}/mo</span> · End: <span className="font-mono font-bold">{proposal.tenant_counter.end_date}</span> · Deposit: <span className="font-mono font-bold">${Number(proposal.tenant_counter.deposit || 0).toLocaleString()}</span>
              </div>
              {proposal.tenant_counter.tenant_message ? (
                <div className="text-amber-900 mt-1 italic">"{proposal.tenant_counter.tenant_message}"</div>
              ) : null}
            </div>
          ) : null}

          <StatusPill status={proposal.status} />
        </div>

        {isPending ? (
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-slate-600">Optional message to your landlord</span>
              <textarea
                rows={3}
                className="mt-2 w-full px-3 py-2 border border-slate-200 rounded-sm text-sm"
                placeholder="Anything you'd like to add — questions, scheduling, etc."
                value={tenantMessage}
                onChange={(e) => setTenantMessage(e.target.value)}
                maxLength={1000}
                data-testid="proposal-tenant-message"
              />
            </label>

            {counterOpen ? (
              <div className="mt-5 bg-amber-50/50 border border-amber-200 rounded-sm p-4 space-y-3" data-testid="counter-form">
                <div className="text-xs uppercase tracking-wider text-amber-900 font-bold">Counter-offer terms</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-slate-600">Monthly rent</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-sm text-sm font-mono"
                      value={counterRent}
                      onChange={(e) => setCounterRent(e.target.value)}
                      data-testid="counter-rent-input"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-slate-600">End date</span>
                    <USDateInput
                      className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-sm text-sm font-mono"
                      value={counterEnd}
                      onChange={(iso) => setCounterEnd(iso)}
                      data-testid="counter-end-input"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-slate-600">Deposit</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-sm text-sm font-mono"
                      value={counterDeposit}
                      onChange={(e) => setCounterDeposit(e.target.value)}
                      data-testid="counter-deposit-input"
                    />
                  </label>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => respond("counter")}
                    disabled={!!submitting}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 text-white px-4 py-2.5 text-sm font-bold rounded-sm transition-colors"
                    data-testid="counter-submit"
                  >
                    <Repeat className="w-4 h-4" />
                    {submitting === "counter" ? "Sending counter…" : "Send counter-offer"}
                  </button>
                  <button
                    onClick={() => setCounterOpen(false)}
                    disabled={!!submitting}
                    className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 border border-slate-300 text-slate-700 px-4 py-2.5 text-sm font-bold rounded-sm"
                    data-testid="counter-cancel"
                  >
                    Cancel counter
                  </button>
                </div>
              </div>
            ) : null}

            <div className="flex flex-col sm:flex-row gap-3 mt-5">
              <button
                onClick={() => respond("accept")}
                disabled={!!submitting}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white px-4 py-3 text-sm font-bold rounded-sm transition-colors"
                data-testid="proposal-accept"
              >
                <CheckCircle2 className="w-4 h-4" />
                {submitting === "accept" ? "Accepting…" : "Accept renewal"}
              </button>
              {!counterOpen ? (
                <button
                  onClick={() => setCounterOpen(true)}
                  disabled={!!submitting}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-amber-50 hover:bg-amber-100 border border-amber-300 text-amber-900 px-4 py-3 text-sm font-bold rounded-sm transition-colors"
                  data-testid="proposal-counter"
                >
                  <Repeat className="w-4 h-4" />
                  Counter-offer
                </button>
              ) : null}
              <button
                onClick={() => respond("decline")}
                disabled={!!submitting}
                className="flex-1 inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-slate-500 text-slate-700 px-4 py-3 text-sm font-bold rounded-sm transition-colors"
                data-testid="proposal-decline"
              >
                <XCircle className="w-4 h-4" />
                {submitting === "decline" ? "Sending…" : "Decline"}
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-4">
              Offer expires {proposal.expires_at ? proposal.expires_at.slice(0, 10) : "soon"} · This page is private — only people with this link can respond.
            </p>
          </div>
        ) : null}

        {isResolved || isCountered ? (
          <div className="text-sm text-slate-500" data-testid="proposal-resolved-confirmation">
            {isCountered
              ? "Your counter-offer has been sent. Watch your inbox — your landlord will respond shortly."
              : "Thanks for your response — your landlord has been notified."}
          </div>
        ) : null}
        {proposal.status === "expired" ? (
          <div className="border border-amber-200 bg-amber-50 text-amber-800 text-sm rounded-sm p-4 inline-flex items-center gap-2">
            <Clock className="w-4 h-4" /> This offer expired on {proposal.expires_at?.slice(0, 10)} — please ask your landlord to send a fresh one.
          </div>
        ) : null}
      </div>
    </Page>
  );
}

function Page({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 py-12 px-6">
      {children}
    </div>
  );
}

function Row({ label, value, sub, mono, testid }) {
  return (
    <div className="flex justify-between items-baseline gap-4 py-2 border-b border-slate-100 last:border-0">
      <span className="text-xs uppercase tracking-wider text-slate-500 shrink-0">{label}</span>
      <span className="text-right">
        <span className={`font-semibold text-slate-900 ${mono ? "font-mono" : ""}`} data-testid={testid}>{value}</span>
        {sub ? <div className="text-xs text-slate-400">{sub}</div> : null}
      </span>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: { tone: "bg-slate-100 text-slate-700 border-slate-200", label: "Awaiting your response" },
    accepted: { tone: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Accepted" },
    declined: { tone: "bg-rose-100 text-rose-800 border-rose-200", label: "Declined" },
    countered: { tone: "bg-amber-100 text-amber-800 border-amber-200", label: "Counter-offer pending" },
    expired: { tone: "bg-amber-100 text-amber-800 border-amber-200", label: "Expired" },
  };
  const cfg = map[status] || map.pending;
  return (
    <div className="mt-5 pt-4 border-t border-slate-100">
      <span className={`inline-block text-[11px] uppercase tracking-wider px-2 py-1 rounded-sm border ${cfg.tone}`} data-testid="proposal-status-pill">
        {cfg.label}
      </span>
    </div>
  );
}
